import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MessageSquare, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProjectContext } from "@/context/project";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export default function Home() {
  const navigate = useNavigate();
  const { recentProjects, setActiveProject } = React.useContext(ProjectContext);

  React.useEffect(() => {
    setActiveProject(null);
  }, [setActiveProject]);

  React.useEffect(() => {
    const prompt = localStorage.getItem("buzooka_prompt");
    const node = localStorage.getItem("node");

    if (prompt) {
      navigate("/chat");
    } else if (node) {
      navigate("/new");
    }
  }, [navigate]);

  return (
    <div className="mx-auto flex h-full w-full max-w-7xl flex-col gap-8 p-8 duration-500 animate-in fade-in">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="flex items-center gap-3 text-4xl font-bold tracking-tight">
          <img
            src="/buzooka-wordmark-ai.svg"
            alt="Buzooka"
            className="h-10 w-auto"
          />
        </h1>
        <p className="text-lg text-muted-foreground">
          Manage your projects or create new.
        </p>
      </div>

      {/* Continue with Recent Projects */}
      {recentProjects.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold tracking-tight">
            Continue with
          </h2>
          <div className="grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
            {recentProjects.map((project) => (
              <Card
                key={project.id}
                className="group cursor-pointer bg-card/50 backdrop-blur-sm transition-all hover:border-primary/50 hover:shadow-md"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{project.name}</span>
                    <ArrowRight className="h-5 w-5 transform text-muted-foreground transition-colors group-hover:translate-x-1 group-hover:text-primary" />
                  </CardTitle>
                  <CardDescription>
                    {project.domainName || "No domain configured"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div
                      className={`h-2 w-2 rounded-full ${project.status === "ready" ? "bg-green-500" : "bg-yellow-500"}`}
                    />
                    Status:{" "}
                    <span className="font-medium text-foreground capitalize">
                      {project.status}
                    </span>
                  </div>
                </CardContent>
                <CardFooter className="pt-0">
                  <p className="text-xs text-muted-foreground">
                    Last updated:{" "}
                    {project.createdAt
                      ? new Date(project.createdAt).toLocaleDateString()
                      : "N/A"}
                  </p>
                </CardFooter>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Main Actions Grid */}
      <section className="flex-1 space-y-4">
        <h2 className="text-xl font-semibold tracking-tight">Get Started</h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Chat with Architect */}
          <Card
            className="group flex h-full cursor-pointer flex-col justify-between bg-gradient-to-br from-card to-card/50 transition-all hover:border-primary/50 hover:shadow-lg"
            onClick={() => navigate("/chat")}
          >
            <CardHeader className="space-y-4">
              <div className="w-fit rounded-lg bg-primary/10 p-3 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <MessageSquare className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <CardTitle>Chat with Architect</CardTitle>
                <CardDescription>
                  Design your system architecture interactively with our AI
                  agent.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                <li>Natural language requirements</li>
                <li>Instant architecture visualization</li>
                <li>Smart stack recommendations</li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button className="w-full group-hover:bg-primary/90">
                Start AI Chat
              </Button>
            </CardFooter>
          </Card>

          {/* Create New Project */}
          <Card
            className="group flex h-full cursor-pointer flex-col justify-between bg-card/50 transition-all hover:border-primary/50 hover:shadow-lg"
            onClick={() => navigate("/new")}
          >
            <CardHeader className="space-y-4">
              <div className="w-fit rounded-lg bg-secondary p-3 text-secondary-foreground transition-colors group-hover:bg-secondary/80">
                <Plus className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <CardTitle>Create New Project</CardTitle>
                <CardDescription>
                  Start a new project from scratch with manual configuration.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                <li>Choose your stack</li>
                <li>Configure services manually</li>
                <li>Full control over setup</li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full">
                Start from Scratch
              </Button>
            </CardFooter>
          </Card>

          {/* OpenClaw AI */}
          {/* <Card
            className="group flex h-full cursor-pointer flex-col justify-between bg-card/50 transition-all hover:border-primary/50 hover:shadow-lg"
            onClick={() => navigate("/new")}
          >
            <CardHeader className="space-y-4">
              <div className="w-fit rounded-lg bg-[#ff4d4d]/10 p-3 text-[#ff4d4d] transition-colors group-hover:bg-[#ff4d4d] group-hover:text-white">
                <Bot className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <CardTitle>OpenClaw AI</CardTitle>
                <CardDescription>
                  Deploy your own autonomous AI agent using OpenClaw (formerly
                  Moltbot).
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                <li>One-click deployment</li>
                <li>Persistent memory</li>
                <li>Full system access</li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                variant="outline"
                className="w-full group-hover:bg-[#ff4d4d] group-hover:text-white"
              >
                Deploy Agent
              </Button>
            </CardFooter>
          </Card> */}

          {/* Placeholder for future cards - visual space */}
          {/* <div className="border border-dashed border-muted-foreground/20 rounded-xl flex items-center justify-center min-h-[300px] text-muted-foreground/40 text-sm">
              More options coming soon...
           </div> */}
        </div>
      </section>
    </div>
  );
}
